<template>
  <div :dir="dir" class="v-select" :class="stateClasses">
    <div ref="toggle" @mousedown.stop="onToggleClick" class="vs__dropdown-toggle">

      <div class="vs__selected-options" ref="selectedOptions">
        <slot v-for="option in selectedValue"
              name="selected-option-container"
              :option="normalizeOptionForSlot(option)"
              :deselect="deselect"
              :multiple="multiple"
              :disabled="disabled">
          <span class="vs__selected" v-bind:key="option.index">
            <slot name="selected-option" v-bind="normalizeOptionForSlot(option)">
              {{ getOptionLabel(option) }}
            </slot>
            <button v-if="multiple" :disabled="disabled" @click="deselect(option)" type="button" class="vs__deselect" aria-label="Deselect option">
              <component :is="childComponents.Deselect" />
            </button>
          </span>
        </slot>

        <slot name="search" v-bind="scope.search">

          <input :class="['vs__search', addressesSelect ? 'only-input' : '']" v-bind="scope.search.attributes" v-on="scope.search.events">
        </slot>
      </div>

      <div class="vs__actions">
        <button
          v-show="showClearButton"
          :disabled="disabled"
          @click="clearSelection"
          type="button"
          class="vs__clear"
          title="Clear selection"
        >
          <component :is="childComponents.Deselect" />
        </button>

        <slot name="open-indicator" v-bind="scope.openIndicator">
          <component :is="childComponents.OpenIndicator" v-if="!noDrop" v-bind="scope.openIndicator.attributes"/>
        </slot>

        <slot name="spinner" v-bind="scope.spinner">
          <div class="vs__spinner" v-show="mutableLoading">Loading...</div>
        </slot>
      </div>
    </div>

    <transition :name="transition">
      <ul ref="dropdownMenu" v-if="dropdownOpen" class="vs__dropdown-menu" role="listbox" @mousedown="onMousedown" @mouseup="onMouseUp">
        <slot name="list-header" v-bind="scope.listHeader" />
        <li
          role="option"
          v-for="(option, index) in filteredOptions"
          :key="index"
          class="vs__dropdown-option"
          :class="{ 'vs__dropdown-option--selected': isOptionSelected(option), 'vs__dropdown-option--highlight': index === typeAheadPointer, 'vs__dropdown-option--disabled': option.disabled }"
          @mouseover="typeAheadPointer = index"
          @mousedown.prevent.stop="select(option)"
        >
          <slot name="option" v-bind="normalizeOptionForSlot(option)">
            {{ getOptionLabel(option) }}
          </slot>
        </li>
        <infinite-loading
          v-if="infiniteLoading"
          ref="infiniteLoading"
          @infinite="infiniteHandler">
          <span slot="no-more" />
          <span slot="no-results" />
        </infinite-loading>
        <li v-if="!filteredOptions.length" class="vs__no-options" @mousedown.stop="">
          <slot name="no-options">Sorry, no matching options.</slot>
        </li>
        <slot name="list-footer" v-bind="scope.listFooter" />
      </ul>
    </transition>
  </div>
</template>

<style>
.vs__dropdown-option--disabled {
  color: #cccccc !important;
  pointer-events: none !important;
  cursor: not-allowed;
}
</style>

<script>
/* eslint-disable */
import pointerScroll from './pointerScroll'
import typeAheadPointer from './typeAheadPointer'
import ajax from './ajax'
import childComponents from './childComponents'
import InfiniteLoading from 'vue-infinite-loading'

export default {
  components: {
    ...childComponents,
    InfiniteLoading
  },
  mixins: [pointerScroll, typeAheadPointer, ajax],
  props: {
    value: {
      type: [String, Number, Object, Array],
      default: 0,
    },
    components: {
      type: Object,
      default: () => ({}),
    },
    option: {
      type: Object,
      required: false
    },
    options: {
      type: Array,
      default() {
        return []
      },
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: true
    },
    searchable: {
      type: Boolean,
      default: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    // --- patch: addressesSelect prop (включает особое поведение)
    addressesSelect: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    selectable: {
      type: Function,
      default: option => true,
    },
    transition: {
      type: String,
      default: 'vs__fade'
    },
    clearSearchOnSelect: {
      type: Boolean,
      default: true
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    label: {
      type: String,
      default: 'label'
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    reduce: {
      type: Function,
      default: option => option,
    },
    fetchData: {
      type: Function,
      required: false
    },
    infiniteLoading: {
      type: Boolean,
      default: true
    },
    getOptionLabel: {
      type: Function,
      default(option) {
        if (typeof option === 'object') {
          if (!option.hasOwnProperty(this.label)) {
            return console.warn(
              `[vue-select warn]: Label key "option.${this.label}" does not` +
              ` exist in options object ${JSON.stringify(option)}.\n` +
              'http://sagalbot.github.io/vue-select/#ex-labels'
            )
          }
          return option.label || option[this.label]
        }
        return option.label || option;
      }
    },
    onTab: {
      type: Function,
      default: function () {
        if (this.selectOnTab) {
          this.typeAheadSelect();
        }
      },
    },
    taggable: {
      type: Boolean,
      default: false
    },
    tabindex: {
      type: Number,
      default: null
    },
    pushTags: {
      type: Boolean,
      default: false
    },
    filterable: {
      type: Boolean,
      default: true
    },
    filterBy: {
      type: Function,
      default(option, label, search) {
        return (label || '').toLowerCase().indexOf(search.toLowerCase()) > -1
      }
    },
    filter: {
      type: Function,
      default(options, search) {
        return options.filter((option) => {
          let label = this.getOptionLabel(option)
          if (typeof label === 'number') {
            label = label.toString()
          }
          return this.filterBy(option, label, search)
        });
      }
    },
    createOption: {
      type: Function,
      default(newOption) {
        if (typeof this.optionList[0] === 'object') {
          newOption = {[this.label]: newOption}
        }
        this.$emit('option:created', newOption)
        return newOption
      }
    },
    resetOnOptionsChange: {
      type: Boolean,
      default: false
    },
    noDrop: {
      type: Boolean,
      default: false
    },
    inputId: {
      type: String
    },
    dir: {
      type: String,
      default: 'auto'
    },
    selectOnTab: {
      type: Boolean,
      default: false
    },
    searchInputQuerySelector: {
      type: String,
      default: '[type=search]'
    }
  },
  data() {
    return {
      search: '',
      open: false,
      pushedTags: [],
      _value: [], // Internal value managed by Vue Select if no `value` prop is passed

      _programmaticFocus: false,
      // PATCH: flag to remember if user typed into search (don't overwrite their input)
      _userEditedSearch: false,
    }
  },
  watch: {
    options: {
      handler() {
        if (!this.taggable && this.resetOnOptionsChange) {
          this.clearSelection()
        }
        if (this.option) {
          const ids = this.options.map(option => JSON.stringify(this.reduce(option)))
          ids.forEach((id, index) => {
            if (ids.slice(0, index).includes(id)) {
              this.options.splice(index, 1)
            }
          })
        }
      },
      deep: true
    },
    option: {
      handler() {
        if (!this.option) {
          return false
        }
        const ids = this.options.map(option => JSON.stringify(this.reduce(option)))
        const optionId = JSON.stringify(this.reduce(this.option))
        if (!ids.includes(optionId)) {
          this.options.push(this.option)
        }
      },
      deep: true
    },
    multiple() {
      this.clearSelection()
    },
    value: {
      handler() {
        this.init()
        // PATCH: when value changed externally, prepopulate search (if addressesSelect)
        if (this.addressesSelect) {
          this.prepopulateSearch()
        }
      },
      deep: true
    },
    reduce: {
      handler() {
        this.init()
      }
    },
    // PATCH: when dropdown open state changes — try to prepopulate (on open/close)
    open(newVal, oldVal) {
      if (this.addressesSelect && newVal === true) {
        this.$nextTick(() => { this.prepopulateSearch() })
      }
    },
    search() {
      if (this.infiniteLoading) {
        // reset infinite loader when search changes
        if (this.$refs && this.$refs.infiniteLoading && this.$refs.infiniteLoading.stateChanger) {
          this.$refs.infiniteLoading.stateChanger.reset()
        }
      }
    }
  },
  mounted() {
    // initial fetch if needed
    if (!this.infiniteLoading) {
      this.fetchData()
    }

    // On mount, initialize and prepopulate for addressesSelect
    this.init()
    if (this.addressesSelect) {
      // nextTick to ensure refs are set
      this.$nextTick(() => {
        this.prepopulateSearch()
      })
    }

    document.addEventListener('mousedown', this.handleDocumentMousedown);
  },

  beforeDestroy() {
    // cleanup
    document.removeEventListener('mousedown', this.handleDocumentMousedown);
  },
  created() {
    this.init()
    this.$on('option:created', this.maybePushTag)
  },
  methods: {
    handleDocumentMousedown(event) {
      // если клик внутри нашего компонента — ничего не делаем
      if (!this.$el) return;

      // Проверяем, кликнули ли на элемент v-select (в т.ч. на vs__selected, vs__dropdown-toggle и т.д.)
      let targetElement = event.target;
      let isVSelectClick = false;

      // Проверяем текущий элемент и всех его родителей
      while (targetElement && targetElement !== document.body) {
        if (targetElement.classList && (
          targetElement.classList.contains('v-select') ||
          targetElement.classList.contains('vs__dropdown-toggle') ||
          targetElement.classList.contains('vs__selected') ||
          targetElement.classList.contains('vs__selected-options')
        )) {
          isVSelectClick = true;
          break;
        }
        targetElement = targetElement.parentElement;
      }

      const clickedInside = this.$el.contains(event.target) || isVSelectClick;

      if (!clickedInside) {
        // если открыт — закрываем
        if (this.open) {
          this.open = false;
        }

        // аккуратно снимем фокус с инпута (если он есть)
        const el = this.searchEl;
        if (el && typeof el.blur === 'function') {
          try { el.blur(); } catch (err) { /* ignore */ }
        }

        // сбросим флаг mousedown, если он мог быть выставлен
        this.mousedown = false;
      }
    },

    init() {
      this.mutableLoading = this.loading;
      if (this.$options.propsData.hasOwnProperty('reduce') && this.value) {
        if (Array.isArray(this.value)) {
          this.$data._value = this.value.map(value => this.findOptionFromReducedValue(value));
        } else {
          this.$data._value = this.findOptionFromReducedValue(this.value);
        }
      }
    },

    // PATCH: prepopulate search input with label of selected address and focus it
    prepopulateSearch() {
      // only for addressesSelect mode
      if (!this.addressesSelect) return;

      // If user already typed something, do not overwrite
      if (this._userEditedSearch) return;

      // PATCH: Если search уже содержит значение (в том числе неполный адрес), не перезаписываем
      if (typeof this.search === 'string' && this.search.length > 0) {
        // Если есть текущее значение и оно совпадает с label выбранного - ничего не делаем
        const selected = this.selectedValue && this.selectedValue.length ? this.selectedValue[0] : null;
        if (selected) {
          const label = this.getOptionLabel(selected);
          if (this.search === label) return;
        }
        // Если search не пустой но не совпадает с label - НЕ перезаписываем (пользователь редактирует)
        return;
      }

      const selected = this.selectedValue && this.selectedValue.length ? this.selectedValue[0] : null;
      if (!selected) return;

      // Use getOptionLabel to obtain displayable text
      const label = this.getOptionLabel(selected);
      if (label == null) return;

      // Set search and focus the input (if available)
      this.search = label;
      // this.$nextTick(() => {
      //   const el = this.searchEl;
      //   if (el && typeof el.focus === 'function') {
      //     this._programmaticFocus = true;
      //     try { el.focus(); } finally {
      //       // сбросим через следующий тик, чтобы onSearchFocus успел увидеть флаг
      //       this.$nextTick(() => { this._programmaticFocus = false });
      //     }
      //   }
      // });
    },

    select(option) {
      if (!this.isOptionSelected(option)) {
        if (this.taggable && !this.optionExists(option)) {
          option = this.createOption(option)
        }
        if (this.multiple) {
          option = this.selectedValue.concat(option)
        }
        this.updateValue(option);
      }
      this.onAfterSelect(option)
    },
    deselect (option) {
      this.updateValue(this.selectedValue.filter(val => {
        return !this.optionComparator(val, option);
      }));
    },
    clearSelection() {
      this.updateValue(this.multiple ? [] : null)
    },
    onAfterSelect(option) {
      if (this.closeOnSelect) {
        this.open = false
        // try blur safely (searchEl may be null)
        const el = this.searchEl;
        if (el && typeof el.blur === 'function') el.blur()
      }
      if (this.clearSearchOnSelect) {
        this.search = ''
      }
    },
    updateValue (value) {
      if (this.isTrackingValues) {
        // Vue select has to manage value
        this.$data._value = value;
      }

      if (value !== null) {
        if (Array.isArray(value)) {
          value = value.map(val => this.reduce(val));
        } else {
          value = this.reduce(value);
        }
      }

      this.$emit('input', value);
    },

    onToggleClick(e) {
      // Всегда открываем/закрываем дропдаун при клике
      this.toggleDropdown(e)
    },

    toggleDropdown (e) {
      const target = e.target;
      const toggleRef = this.$refs.toggle && this.$refs.toggle.$el ? this.$refs.toggle.$el : this.$refs.toggle;

      // Проверяем, был ли клик внутри vs__dropdown-toggle
      const clickedInsideToggle = toggleRef && toggleRef.contains(target);

      if (clickedInsideToggle) {
        if (this.open) {
          this.open = false;
          const el = this.searchEl;
          if (el && typeof el.blur === 'function') el.blur();
        } else {
          if (!this.disabled) {
            this.open = true;
            if (this.searchable) {
              this.$nextTick(() => {
                const el = this.searchEl;
                if (el && typeof el.focus === 'function') {
                  el.focus();
                }
              });
            }
          }
        }
      }
    },
    isOptionSelected(option) {
      return this.selectedValue.some(value => {
        return this.optionComparator(value, option)
      })
    },
    optionComparator(value, option) {
      if (typeof value !== 'object' && typeof option !== 'object') {
        // Comparing primitives
        if (value === option) {
          return true
        }
      } else {
        // Comparing objects
        if (value === this.reduce(option)) {
          return true
        }
        if ((this.getOptionLabel(value) === this.getOptionLabel(option)) || (this.getOptionLabel(value) === option)) {
          return true
        }
        if (this.reduce(value) === this.reduce(option)) {
          return true
        }
      }
      return false;
    },
    findOptionFromReducedValue (value) {
      return this.options.find(option => JSON.stringify(this.reduce(option)) === JSON.stringify(value)) || value;
    },
    closeSearchOptions(){
      this.open = false
      this.$emit('search:blur')
    },
    maybeDeleteValue() {
      // prefer using reactive `search` instead of direct DOM read to avoid undefined refs
      const searchValue = (typeof this.search === 'string') ? this.search : ''
      const hasSelected = Array.isArray(this.selectedValue) ? this.selectedValue.length > 0 : !!this.selectedValue
      if (!searchValue.length && hasSelected && this.clearable) {
        let value = null;
        if (this.multiple) {
          value = [...this.selectedValue.slice(0, this.selectedValue.length - 1)]
        }
        this.updateValue(value)
      }
    },
    optionExists(option) {
      return this.optionList.some(opt => {
        if (typeof opt === 'object' && this.getOptionLabel(opt) === option) {
          return true
        } else if (opt === option) {
          return true
        }
        return false
      })
    },
    normalizeOptionForSlot (option) {
      return (typeof option === 'object') ? option : {[this.label]: option.label};
    },
    maybePushTag(option) {
      if (this.pushTags) {
        this.pushedTags.push(option)
      }
    },
    onEscape() {
      if (!this.search.length) {
        const el = this.searchEl;
        if (el && typeof el.blur === 'function') el.blur();
      } else {
        this.search = ''
      }
    },
    onSearchBlur() {
      if (this.mousedown && !this.searching) {
        this.mousedown = false
      } else {
        // PATCH: для addressesSelect не очищаем search при blur, сохраняем текущее значение
        if (this.clearSearchOnBlur && !this.addressesSelect) {
          this.search = ''
        }
        this.closeSearchOptions()
        // reset user-edited flag on blur. Even if user typed, `search` will not be empty
        // so subsequent prepopulate won't overwrite non-empty search.
        this._userEditedSearch = false
        return
      }
      // Fixed bug where no-options message could not be closed
      if (this.search.length === 0 && this.options.length === 0){
        this.closeSearchOptions()
        this._userEditedSearch = false
        return
      }

      this._userEditedSearch = false
    },
    onSearchFocus() {
      // если это програмный focus — не открываем дропдаун
      if (this._programmaticFocus) {
        return;
      }
      this.open = true
      this.$emit('search:focus')
    },
    onMousedown() {
      this.mousedown = true
    },
    onMouseUp() {
      this.mousedown = false
    },
    onSearchKeyDown (e) {
      switch (e.keyCode) {
        case 8:
          //  delete
          return this.maybeDeleteValue();
        case 9:
          //  tab
          return this.onTab();
      }
    },
    onSearchKeyUp (e) {
      switch (e.keyCode) {
        case 27:
          //  esc
          return this.onEscape();
        case 38:
          //  up.prevent
          e.preventDefault();
          return this.typeAheadUp();
        case 40:
          //  down.prevent
          e.preventDefault();
          return this.typeAheadDown();
        case 13:
          //  enter.prevent
          e.preventDefault();
          return this.typeAheadSelect();
      }
    },
    async infiniteHandler($state) {
      if (!this.fetchData) {
        if (this.infiniteLoading) {
          $state.complete()
        }
        return false
      }
      const response = await this.fetchData()
      if (this.infiniteLoading) {
        $state.loaded()
        if (response.total < response.limit * response.page) {
          $state.complete()
        }
      }
    }
  },
  computed: {
    isTrackingValues () {
      return (typeof this.value === 'undefined' || this.$options.propsData.hasOwnProperty('reduce')) && this.value !== null && this.value != '';
    },
    selectedValue () {
      let value = this.value;
      if (this.isTrackingValues) {
        // Vue select has to manage value internally
        value = this.$data._value;
      }
      if (value && value !== '' && value !== null) {
        return [].concat(value);
      }
      return [];
    },
    optionList () {
      return this.options.concat(this.pushedTags);
    },
    searchEl() {
      // robust getter for search element:
      // - if user provided a scoped slot for `search`, we try to find the input inside selectedOptions
      // - otherwise we expect the default input to have ref="search" via scope.search.attributes
      try {
        if (this.$scopedSlots && this.$scopedSlots['search']) {
          // for addressesSelect variant selectedOptions might be elsewhere - try both
          const container = this.$refs.selectedOptions || this.$refs.selectedAddressesOptions || this.$el;
          if (container && container.querySelector) {
            return container.querySelector(this.searchInputQuerySelector)
          }
          return null
        } else {
          return this.$refs.search || null
        }
      } catch (err) {
        return this.$refs.search || null
      }
    },
    scope () {
      const listSlot = {
        search: this.search,
        loading: this.loading,
        searching: this.searching,
        filteredOptions: this.filteredOptions,
      }
      return {
        search: {
          attributes: {
            'disabled': this.disabled,
            'placeholder': this.searchPlaceholder,
            'tabindex': this.tabindex,
            'readonly': !this.searchable,
            'id': this.inputId,
            'aria-expanded': this.dropdownOpen,
            'aria-label': 'Search for option',
            'ref': 'search',
            'role': 'combobox',
            'type': 'search',
            'autocomplete': 'off',
            'value': this.search,
          },
          events: {
            'keydown': this.onSearchKeyDown,
            'keyup': this.onSearchKeyUp,
            'blur': this.onSearchBlur,
            'focus': this.onSearchFocus,
            // mark that user edited the search when typing
            'input': (e) => { this.search = e.target.value; this._userEditedSearch = true; }
          },
        },
        spinner: {
          loading: this.mutableLoading
        },
        openIndicator: {
          attributes: {
            'ref': 'openIndicator',
            'role': 'presentation',
            'class': 'vs__open-indicator',
          },
        },
        listHeader: listSlot,
        listFooter: listSlot,
      };
    },
    childComponents () {
      return {
        ...childComponents,
        ...this.components
      };
    },
    stateClasses() {
      return {
        'vs--open': this.dropdownOpen,
        'vs--single': !this.multiple,
        'vs--searching': this.searching && !this.noDrop,
        'vs--searchable': this.searchable && !this.noDrop,
        'vs--unsearchable': !this.searchable,
        'vs--loading': this.mutableLoading,
        'vs--disabled': this.disabled,
        'vs__dropdown-option--disabled': !this.selectable,
      }
    },
    clearSearchOnBlur() {
      return this.clearSearchOnSelect && !this.multiple
    },
    searching() {
      return !! this.search
    },
    dropdownOpen() {
      return this.noDrop ? false : this.open && !this.mutableLoading
    },
    searchPlaceholder() {
      if (this.isValueEmpty && this.placeholder) {
        return this.placeholder;
      }
    },
    filteredOptions() {
      const optionList = [].concat(this.optionList);
      if (!this.filterable && !this.taggable) {
        return optionList;
      }
      let options = optionList
      if (this.taggable && this.search.length && !this.optionExists(this.search)) {
        options.unshift(this.search)
      }
      this.$emit('filtered-options', options)
      return options
    },
    isValueEmpty() {
      return this.selectedValue.length === 0;
    },
    showClearButton() {
      return !this.multiple && this.clearable && !this.open && !this.isValueEmpty
    }
  },
}
</script>
